import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { DashboardLayout } from '@/components/DashboardLayout';
import { api } from '@/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, MapPin, ExternalLink, FolderKanban, ArrowLeft, RefreshCw } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icons (webpack breaks them)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700',
  completed: 'bg-blue-100 text-blue-700',
  on_hold: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-red-100 text-red-700',
};

export default function ProjectMap() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/projects');
      setProjects(res.data || []);
    } catch (err) {
      console.error('Failed to load projects', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const mappedProjects = projects.filter(p => p.location?.lat && p.location?.lng);
  const unmappedProjects = projects.filter(p => !p.location?.lat || !p.location?.lng);

  // Center map on South Africa by default, or on first project
  const defaultCenter = [-30.5595, 22.9375];
  const defaultZoom = 5;
  const center = mappedProjects.length > 0
    ? [mappedProjects[0].location.lat, mappedProjects[0].location.lng]
    : defaultCenter;
  const zoom = mappedProjects.length > 0 ? 8 : defaultZoom;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate('/')} className="rounded-sm" data-testid="map-back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-barlow font-bold uppercase tracking-wider text-slate-900">
              Project Map
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {mappedProjects.length} project{mappedProjects.length !== 1 ? 's' : ''} on map
              {unmappedProjects.length > 0 && ` · ${unmappedProjects.length} without location`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadData} className="rounded-sm" data-testid="map-refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Badge variant="outline" className="rounded-sm text-xs">
            <MapPin className="h-3 w-3 mr-1" />
            {mappedProjects.length} Pinned
          </Badge>
        </div>
      </div>

      {/* Map */}
      <Card className="border-slate-200 overflow-hidden" data-testid="project-map-card">
        <div style={{ height: '520px', width: '100%' }}>
          <MapContainer
            center={center}
            zoom={zoom}
            minZoom={3}
            maxBounds={[[-85, -180], [85, 180]]}
            maxBoundsViscosity={1.0}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              noWrap={true}
            />
            {mappedProjects.map((project) => (
              <Marker
                key={project.id}
                position={[project.location.lat, project.location.lng]}
              >
                <Popup>
                  <div className="min-w-[200px]">
                    <p className="font-bold text-sm text-slate-900 mb-1">{project.name}</p>
                    {project.project_number && (
                      <p className="text-xs text-slate-500 mb-1">{project.project_number}</p>
                    )}
                    {project.client_name && (
                      <p className="text-xs text-slate-600 mb-1">Client: {project.client_name}</p>
                    )}
                    <p className="text-xs text-slate-500 mb-2">{project.location.address}</p>
                    <button
                      onClick={() => navigate(`/projects/${project.id}`)}
                      className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <ExternalLink className="h-3 w-3" /> Open Project
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </Card>

      {/* Unmapped projects list */}
      {unmappedProjects.length > 0 && (
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
              Projects Without Location ({unmappedProjects.length})
            </h3>
            <div className="space-y-2">
              {unmappedProjects.map((project) => (
                <div
                  key={project.id}
                  className="flex items-center justify-between p-2 rounded-sm hover:bg-slate-50 border border-slate-100 cursor-pointer"
                  onClick={() => navigate(`/projects/${project.id}`)}
                  data-testid={`unmapped-project-${project.id}`}
                >
                  <div className="flex items-center gap-2">
                    <FolderKanban className="h-4 w-4 text-slate-400" />
                    <span className="text-sm font-medium text-slate-700">{project.name}</span>
                    {project.client_name && (
                      <span className="text-xs text-slate-400">— {project.client_name}</span>
                    )}
                  </div>
                  <Badge variant="outline" className={`rounded-sm text-[10px] ${STATUS_COLORS[project.status] || ''}`}>
                    {project.status || 'active'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
    </DashboardLayout>
  );
}
